import { AccessCodeDropdownItem } from './accessCode.types'
import { CohortShort } from './cohort.types'
import { Nullish } from './common.types'

export enum UserType {
  ADMIN = 'ADMIN',
  CAREGIVER = 'CAREGIVER',
  CARETEAM = 'CARETEAM',
  TRAINEE = 'TRAINEE',
  ORG_OWNER = 'ORG_OWNER',
  ORG_REGIONAL_MANAGER = 'ORG_REGIONAL_MANAGER',
  ORG_FACILITY_MANAGER = 'ORG_FACILITY_MANAGER',
  ORG_SUPERVISOR = 'ORG_SUPERVISOR',
  ANALYST = 'ANALYST',
  UNASSIGNED = 'UNASSIGNED',
}

export const UserTypeLabels = {
  [UserType.ADMIN]: 'Admin',
  [UserType.CAREGIVER]: 'Caregiver',
  [UserType.CARETEAM]: 'Care Team',
  [UserType.TRAINEE]: 'Trainee',
  [UserType.ORG_OWNER]: 'Level 4',
  [UserType.ORG_REGIONAL_MANAGER]: 'Level 3',
  [UserType.ORG_FACILITY_MANAGER]: 'Level 2',
  [UserType.ORG_SUPERVISOR]: 'Level 1',
  [UserType.ANALYST]: 'Analyst',
  [UserType.UNASSIGNED]: 'Unassigned',
}

export const UserOrganizationalRoles = [
  UserType.ORG_OWNER,
  UserType.ORG_REGIONAL_MANAGER,
  UserType.ORG_FACILITY_MANAGER,
  UserType.ORG_SUPERVISOR,
]

export enum AdminStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const AdminStatusLabels = {
  [AdminStatus.ACTIVE]: 'Active',
  [AdminStatus.INACTIVE]: 'Inactive',
}

export const AdminStatusOptions = Object.values(AdminStatus).map((status) => ({
  value: status,
  label: AdminStatusLabels[status],
}))

export const UserAdminRoles = [UserType.ADMIN, UserType.ANALYST]

export enum UserTypeTab {
  ADMIN = 'ADMIN',
  CAREGIVER = 'CAREGIVER',
  CARETEAM = 'CARETEAM',
  TRAINEE = 'TRAINEE',
  ORG = 'ORG',
}

export type User = {
  id: number
  firstname: string
  lastname: string
  email: string
  password: string
  trainingPathwayTypeName?: string
  createdAt: string
  type: UserType[]
  accessCodeExpiryDate?: string | null
  totalEngagement: string // '04:05:00'
  personOfConcernFirstName: string
  personOfConcernLastName: string
  currentType: UserType
  carePersonId?: number
  isTrainingPathwayTypeChanged: Nullish<boolean>
  role?: string
  licenseExpiredAt?: string | null
  leftOrganizationAt?: string | null
  creator?: User
  accessCode?: {
    id: number
    code: string
    specificUnitName: string
    specificState: string
  }
}

export enum EUserEducation {
  highSchoolOrLess = 'highSchoolOrLess',
  beyondHighSchool = 'beyondHighSchool',
}

export enum EUserExperience {
  lessThan5Years = 'lessThan5Years',
  equalOrGreaterThan5Years = 'equalOrGreaterThan5Years',
}

export enum EUserLanguage {
  english = 'english',
  spanish = 'spanish',
  chinese = 'chinese',
  filipino = 'filipino',
  other = 'other',
}

export enum ECareLevel {
  veryLittle = 'veryLittle',
  fewThings = 'fewThings',
  mostThings = 'mostThings',
  withAll = 'withAll',
}

export enum ETimelineStatus {
  none = 'none',
  inProgress = 'inProgress',
}

export enum ELearnLibraryStatus {
  none = 'none',
  inProgress = 'inProgress',
}

export enum EUserHighestDegree {
  preferNotToSay = 'preferNotToSay',
  someHighSchool = 'someHighSchool',
  highSchoolDiploma = 'highSchoolDiploma',
  associateDegree = 'associateDegree',
  bachelorDegree = 'bachelorDegree',
  masterDegree = 'masterDegree',
  phDOrMD = 'phDOrMD',
  other = 'other',
}

export enum EUserRacialDesignation {
  preferNotToSay = 'preferNotToSay',
  africanAmericanOrBlack = 'africanAmericanOrBlack',
  arabAmericanOrMiddleEastern = 'arabAmericanOrMiddleEastern',
  asianAmericanOrSouthAsian = 'asianAmericanOrSouthAsian',
  hispanicOrLatino = 'hispanicOrLatino',
  nativeAmericanOrAlaskaNative = 'nativeAmericanOrAlaskaNative',
  nativeHawaiianOrPacificIslander = 'nativeHawaiianOrPacificIslander',
  white = 'white',
  other = 'other',
}

export enum EUserAgeGroups {
  AgeLessThan20 = 'AgeLessThan20',
  Age20To29 = 'Age20To29',
  Age30To39 = 'Age30To39',
  Age40To49 = 'Age40To49',
  Age50To59 = 'Age50To59',
  Age60To69 = 'Age60To69',
  Age70To79 = 'Age70To79',
  Age80To89 = 'Age80To89',
  AgeGreaterThan90 = 'AgeGreaterThan90',
}

export const UserAgeGroupsLabels = {
  [EUserAgeGroups.AgeLessThan20]: 'Less than 20 years',
  [EUserAgeGroups.Age20To29]: '20-29 years',
  [EUserAgeGroups.Age30To39]: '30-39 years',
  [EUserAgeGroups.Age40To49]: '40-49 years',
  [EUserAgeGroups.Age50To59]: '50-59 years',
  [EUserAgeGroups.Age60To69]: '60-69 years',
  [EUserAgeGroups.Age70To79]: '70-79 years',
  [EUserAgeGroups.Age80To89]: '80-89 years',
  [EUserAgeGroups.AgeGreaterThan90]: 'Greater than 90 years',
}

export type UserModel = {
  id: number
  firstname: string
  lastname: string
  email: string
  cohorts: CohortShort[]
  anonymousId: number
  education: EUserEducation
  experience: EUserExperience
  language: EUserLanguage
  personOfConcernFirstName: string
  personOfConcernLastName: string
  careLevel: Nullish<ECareLevel>
  forgotPasswordSentAt: string
  forgotPasswordToken: string
  trainingPathwayTypeId: number
  trainingPathwayTypeChangedAt: Nullish<string>
  isTrainingPathwayTypeChanged: Nullish<boolean>
  userBasicInfoId: Nullish<number>
  stripeCustomerId: Nullish<number>
  type: UserType[]
  isActive: boolean
  timelineStatus: ETimelineStatus
  learnLibraryStatus: ELearnLibraryStatus
  lastLoggedInAt: string
  totalNumbersOfLogin: number
  carePersonId: Nullish<number>
  createdAt: string
  updatedAt: string
  deletedAt: Nullish<string>
  // survey monkey
  highestDegree: EUserHighestDegree
  otherHighestDegree: string
  racialDesignation: EUserRacialDesignation
  otherRacialDesignation: string
  jobTitle: string
  accessCodeId?: number
  accessCode?: AccessCodeDropdownItem
  leftOrganizationAt?: string
  isUnlicensed?: boolean
  phoneNumber: string
  isEmailNotificationEnabled: boolean
  isSmsNotificationEnabled: boolean
}

export interface UserUpdateRequest {
  id: number
  firstname: string
  lastname: string
  email: string
  cohortIds?: number[]
  type?: UserType[]
  education?: EUserEducation
  experience?: EUserExperience
  language?: EUserLanguage
  highestDegree?: EUserHighestDegree
  otherHighestDegree?: string
  racialDesignation?: EUserRacialDesignation
  otherRacialDesignation?: string
  jobTitle?: string
  accessCodeId?: number | null
  shouldLeaveOrganization?: boolean
  isUnlicensed?: boolean
  customTrainingPathwayTypeId?: number
  phoneNumber?: string
  isEmailNotificationEnabled?: boolean
  isSmsNotificationEnabled?: boolean
}

export interface UserCaregiverUpdateRequest {
  id: number
  firstname: string
  lastname: string
  email: string
  careLevel: ECareLevel
}

export interface UserConfirmPathwayChangesRequest {
  id: number
  isTrainingPathwayTypeChanged: boolean
}

export interface UserPathwayType {
  id: number
  name: string
  title: string
}

export enum EUserUpdateNotice {
  trainingPathwayTypeWillBeChanged = 'trainingPathwayTypeWillBeChanged',
}

export interface UserUpdateNoticeData {
  reason: EUserUpdateNotice
  data: {
    oldTrainingPathwayType: UserPathwayType
    newTrainingPathwayType: UserPathwayType
  }
}

export interface UserHistoryRow {
  user: User
  createdAt: string
  updatedAt: string
  fieldName: string
  id: number
  newValue: string | number
  oldValue: string | number
  updateAction: string
  userId: number
}

export interface UserUpdater {
  id: number
  email: string
  firstname: string
  lastname: string
}

export interface ContactUsRequest {
  email: string
  subject: string
  text?: string
}

export interface ReportUser {
  userId: number
  userEmail: string
  userFirstName: string
  userLastName: string
  userAnonymousId: number
  licenseExpiredAt: string
  isLicenseExpired?: boolean
  userAccessCode: string
  courseId: number
  courseName: string
  activeEngagement: {
    minutes: number
    seconds: number
  }
  userCohorts: CohortShort[]
  completedCourses: number[]
  passedOn: string
  completedCoursesCount: number
}

export interface AddUserRequest {
  name: string
  lastName: string
  email: string
  type?: UserType
  phoneNumber?: string
  organizationId?: number
  shouldSendWelcomeEmail?: boolean
}

export interface UpdateAdminOrAnalystRequest {
  id: number
  name: string
  lastName: string
  email: string
  type: UserType[]
  phoneNumber?: string
  isUnlicensed?: boolean
}

export enum UserOrgTab {
  Details = 'details',
  Roles = 'roles',
}

export interface UserInfoInOrganization {
  id: number
  firstname: string
  lastname: string
  email: string
}

export enum EngagementOrgUserReportTypes {
  TotalEngagement = 'totalEngagement',
  Login = 'login',
}
